import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../common/Card';
import Pagination from '../../common/Pagination';
import { useTheme } from '../../../contexts/ThemeContext';
import { toast } from 'react-toastify';
import certificatService from '../../../services/certificatService';

const CertificatList = ({
  certificats,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onView,
  onEdit,
  onDelete,
  onDownload
}) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState('cards');
  const [downloadingIds, setDownloadingIds] = useState(new Set());

  // Handle download - works for both existing files and API generation
  const handleDownload = async (certificat) => {
    if (downloadingIds.has(certificat.id)) return; // Prevent multiple downloads
    
    setDownloadingIds(prev => new Set([...prev, certificat.id]));
    
    try {
      if (certificat.piecePath) {
        // Use existing download handler if file exists
        await onDownload(certificat);
      } else {
        // Generate and download via API
        const result = await certificatService.downloadCertificat(certificat.id);
        if (result.success) {
          toast.success('Certificat téléchargé avec succès');
        } else {
          toast.error(result.message || 'Erreur lors du téléchargement');
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du téléchargement du certificat');
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(certificat.id);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Always use neutral gray for icon backgrounds/texts in dark mode
  const getCertificatType = (pieceName) => {
    if (!pieceName) return { type: 'Document', color: 'gray', icon: 'fas fa-file' };
    const name = pieceName.toLowerCase();
    if (name.includes('certificat')) {
      return { type: 'Certificat', color: 'green', icon: 'fas fa-certificate' };
    } else if (name.includes('rapport')) {
      return { type: 'Rapport', color: 'blue', icon: 'fas fa-file-alt' };
    } else if (name.includes('pdf')) {
      return { type: 'PDF', color: 'red', icon: 'fas fa-file-pdf' };
    }
    return { type: 'Document', color: 'gray', icon: 'fas fa-file' };
  };

  // Status badge: neutral gray in dark mode, color in light mode
  const getStatusColor = (certificat) => {
    if (!certificat.piecePath && !certificat.pieceName) {
      return isDarkMode
        ? 'bg-gray-700 text-gray-200 border-gray-600'
        : 'bg-gray-100 text-gray-800 border-gray-200';
    }
    return isDarkMode
      ? 'bg-gray-700 text-green-300 border-gray-600'
      : 'bg-green-100 text-green-800 border-green-200';
  };

  const getStatusText = (certificat) => {
    if (!certificat.piecePath && !certificat.pieceName) {
      return 'En attente';
    }
    return 'Disponible';
  };

  // Truncate text helper
  const truncateText = (text, maxLength = 30) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
              <p className="text-gray-600">Chargement des certificats...</p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!certificats || certificats.length === 0) {
    return (
      <Card>
        <div className="p-6">
          <div className="text-center py-12">
            <div className={`mx-auto h-24 w-24 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mb-4`}>
              <i className="fas fa-certificate text-gray-400 text-2xl"></i>
            </div>
            <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Aucun certificat</h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
              Commencez par créer votre premier certificat.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Mode Toggle & Results Info */}
      <Card>
        <div className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className={`flex items-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <i className="fas fa-info-circle mr-2 flex-shrink-0"></i>
              <span className="break-words">
                {pagination.total} certificat{pagination.total > 1 ? 's' : ''} au total
                {pagination.total > pagination.pageSize && (
                  <span className="ml-1 hidden sm:inline">
                    • Page {pagination.page} sur {pagination.maxPage}
                  </span>
                )}
              </span>
            </div>
            {/* View Mode Toggle */}
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} flex rounded-lg p-1 flex-shrink-0`}>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'cards'
                    ? `${isDarkMode ? 'bg-gray-800 text-primary-400 shadow-sm' : 'bg-white text-primary-600 shadow-sm'}`
                    : `${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`
                }`}
              >
                <i className="fas fa-th-large mr-1.5"></i>
                <span className="hidden sm:inline">Cartes</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'table'
                    ? `${isDarkMode ? 'bg-gray-800 text-primary-400 shadow-sm' : 'bg-white text-primary-600 shadow-sm'}`
                    : `${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`
                }`}
              >
                <i className="fas fa-list mr-1.5"></i>
                <span className="hidden sm:inline">Liste</span>
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Cards View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {certificats.map((certificat) => {
            const certType = getCertificatType(certificat.pieceName);
            const isDownloading = downloadingIds.has(certificat.id);
            
            return (
              <Card key={certificat.id} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : ''} hover:shadow-lg transition-shadow duration-200`}>
                <div className="p-4 sm:p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className={
                        `w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mr-3 flex-shrink-0
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-200'
                          : `bg-${certType.color}-100 text-${certType.color}-600`}`
                      }>
                        <i className={`${certType.icon} text-sm sm:text-lg`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} text-sm sm:text-base break-words line-clamp-2`}
                          title={certificat.pieceName || 'Document sans nom'}
                        >
                          {truncateText(certificat.pieceName || 'Document sans nom', 25)}
                        </h3>
                        <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {certificat.id}</p>
                      </div>
                    </div>
                  </div>

                  {/* Client Info */}
                  <div className="mb-4 space-y-1">
                    <div className={`flex items-center text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <i className="fas fa-building mr-2 text-gray-400 flex-shrink-0"></i>
                      <span 
                        className="font-medium truncate flex-1 min-w-0"
                        title={certificat.raisonSocial}
                      >
                        {truncateText(certificat.raisonSocial, 20)}
                      </span>
                    </div>
                    {(certificat.clientFirstName || certificat.clientLastName) && (
                      <div className={`flex items-center text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <i className="fas fa-user mr-2 text-gray-400 flex-shrink-0"></i>
                        <span 
                          className="truncate flex-1 min-w-0"
                          title={`${certificat.clientFirstName || ''} ${certificat.clientLastName || ''}`.trim()}
                        >
                          {truncateText(`${certificat.clientFirstName || ''} ${certificat.clientLastName || ''}`.trim(), 18)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <div className="mb-4">
                    <div className={`flex items-center text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <i className="fas fa-calendar mr-2 text-gray-400 flex-shrink-0"></i>
                      <span className="truncate">{formatDate(certificat.dateCertif)}</span>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="mb-4">
                    <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(certificat)}`}>
                      <div className="w-2 h-2 rounded-full bg-current mr-1.5 flex-shrink-0"></div>
                      {getStatusText(certificat)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => onView(certificat)}
                      className={`flex-1 ${isDarkMode ? 'bg-gray-700 text-primary-400 hover:bg-gray-600' : 'bg-primary-50 text-primary-700 hover:bg-primary-100'} px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors`}
                    >
                      <i className="fas fa-eye mr-1"></i>
                      <span className="hidden sm:inline">Voir</span>
                    </button>
                    <div className="flex gap-2">
                      {/* Download Button - Always visible */}
                      <button
                        onClick={() => handleDownload(certificat)}
                        disabled={isDownloading}
                        className={`${
                          isDownloading
                            ? 'opacity-50 cursor-not-allowed'
                            : isDarkMode 
                              ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                        } px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors`}
                        title={certificat.piecePath ? 'Télécharger' : 'Générer et télécharger'}
                      >
                        {isDownloading ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-download"></i>
                        )}
                      </button>
                      
                      {user?.isAdmin && (
                        <>
                          <button
                            onClick={() => onEdit(certificat)}
                            className={`${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'} px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors`}
                            title="Modifier"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => onDelete(certificat)}
                            className={`${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-red-50 text-red-700 hover:bg-red-100'} px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors`}
                            title="Supprimer"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card>
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>
                      Certificat
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>
                      Client
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>
                      Date
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden sm:table-cell ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>
                      Statut
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={isDarkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}>
                  {certificats.map((certificat) => {
                    const certType = getCertificatType(certificat.pieceName);
                    const isDownloading = downloadingIds.has(certificat.id);
                    
                    return (
                      <tr key={certificat.id} className={isDarkMode ? 'hover:bg-gray-700 transition-colors' : 'hover:bg-gray-50 transition-colors'}>
                        <td className="px-3 sm:px-6 py-4">
                          <div className="flex items-center min-w-0">
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 ${
                              isDarkMode
                                ? 'bg-gray-700 text-gray-200'
                                : `bg-${certType.color}-100 text-${certType.color}-600`
                            }`}>
                              <i className={`${certType.icon} text-xs sm:text-sm`}></i>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div 
                                className={`text-xs sm:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} break-words line-clamp-2`}
                                title={certificat.pieceName || 'Document sans nom'}
                              >
                                {truncateText(certificat.pieceName || 'Document sans nom', 30)}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ID: {certificat.id}
                              </div>
                              {/* Show client info on mobile */}
                              <div className={`md:hidden text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                <div className="truncate" title={certificat.raisonSocial}>
                                  {truncateText(certificat.raisonSocial, 25)}
                                </div>
                                <div className="lg:hidden text-xs text-gray-400 mt-1">
                                  {formatDate(certificat.dateCertif)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 hidden md:table-cell">
                          <div className={`text-xs sm:text-sm font-medium break-words ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            <div 
                              className="truncate"
                              title={certificat.raisonSocial}
                            >
                              {truncateText(certificat.raisonSocial, 25)}
                            </div>
                          </div>
                          {(certificat.clientFirstName || certificat.clientLastName) && (
                            <div 
                              className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                              title={`${certificat.clientFirstName || ''} ${certificat.clientLastName || ''}`.trim()}
                            >
                              {truncateText(`${certificat.clientFirstName || ''} ${certificat.clientLastName || ''}`.trim(), 20)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                          <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {formatDate(certificat.dateCertif)}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                          <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(certificat)}`}>
                            <div className="w-2 h-2 rounded-full bg-current mr-1.5 flex-shrink-0"></div>
                            {getStatusText(certificat)}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                            <button
                              onClick={() => onView(certificat)}
                              className={`${isDarkMode ? 'text-primary-400 hover:text-white' : 'text-primary-600 hover:text-primary-900'} p-1 rounded text-xs sm:text-sm`}
                              title="Voir les détails"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            
                            {/* Download Button - Always visible */}
                            <button
                              onClick={() => handleDownload(certificat)}
                              disabled={isDownloading}
                              className={`${
                                isDownloading
                                  ? 'opacity-50 cursor-not-allowed text-gray-400'
                                  : isDarkMode 
                                    ? 'text-gray-200 hover:text-white' 
                                    : 'text-green-600 hover:text-green-900'
                              } p-1 rounded text-xs sm:text-sm`}
                              title={certificat.piecePath ? 'Télécharger' : 'Générer et télécharger'}
                            >
                              {isDownloading ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-download"></i>
                              )}
                            </button>
                            
                            {user?.isAdmin && (
                              <>
                                <button
                                  onClick={() => onEdit(certificat)}
                                  className={`${isDarkMode ? 'text-gray-200 hover:text-white' : 'text-amber-600 hover:text-amber-900'} p-1 rounded text-xs sm:text-sm`}
                                  title="Modifier"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  onClick={() => onDelete(certificat)}
                                  className={`${isDarkMode ? 'text-gray-200 hover:text-white' : 'text-red-600 hover:text-red-900'} p-1 rounded text-xs sm:text-sm`}
                                  title="Supprimer"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {pagination.maxPage > 1 && (
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : ''} flex justify-center`}>
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.maxPage}
            pageSize={pagination.pageSize}
            totalItems={pagination.total}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </div>
  );
};

export default CertificatList;